import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, CheckCircle2, AlertTriangle, DoorClosed, RectangleHorizontal, LayoutGrid } from "lucide-react";
import { apiGet, sketchUrl } from "@/lib/api";

const tipIcon = (tip) => {
  if (tip === "usa") return DoorClosed;
  if (tip === "panou") return LayoutGrid;
  return RectangleHorizontal;
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-xs font-medium text-slate-800 text-right">{value || "—"}</span>
  </div>
);

export default function ComandaDetaliuPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setC(await apiGet(`/comenzi/${id}`));
    } catch (e) {
      toast.error(e.message);
      navigate("/comenzi");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-20 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!c) return null;

  const a = c.antet || {};
  const culoare = a.color_config?.exterior_cod || a.color_config?.interior_cod || "";

  return (
    <div data-testid="comanda-detaliu-page">
      <button
        onClick={() => navigate("/comenzi")}
        data-testid="back-button"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Inapoi la comenzi
      </button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">{c.nr_comanda}</h1>
          <p className="text-sm text-slate-500 mt-1">{a.client_nume || "(fara client)"}{a.client_localitate ? ` · ${a.client_localitate}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {c.checksum_ok ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Checksum OK</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Checksum diferit</Badge>
          )}
          <Badge variant="secondary">{c.nr_pozitii} pozitii</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-1 h-fit" data-testid="antet-card">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Antet comanda</h2>
          <InfoRow label="Data" value={a.data} />
          <InfoRow label="Serie" value={a.serie} />
          <InfoRow label="Esenta" value={a.esenta} />
          <InfoRow label="Tip geam" value={a.tip_geam} />
          <InfoRow label="Tip lamela" value={a.tip_lamela} />
          <InfoRow label="Culoare" value={culoare} />
          <InfoRow label="Telefon" value={a.client_telefon} />
          <InfoRow label="Email" value={a.client_email} />
          <InfoRow label="Fisier" value={c.fisier_nume} />
          <InfoRow label="Exportat de" value={c.exportat_de} />
        </Card>

        <div className="lg:col-span-2">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Pozitii ({c.pozitii.length})</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pozitii-list">
            {c.pozitii.map((p) => {
              const Icon = tipIcon(p.tip);
              return (
                <Card key={p.id || p.index} className="p-4" data-testid={`pozitie-${p.index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-slate-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">Poz. {p.index}</div>
                        <div className="text-[11px] text-slate-400 capitalize">{p.tip}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[11px]">{p.nr_bucati} buc</Badge>
                  </div>

                  <div className="rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center p-3 min-h-[160px]" data-testid={`sketch-wrap-${p.index}`}>
                    {p.are_sketch ? (
                      <img
                        src={sketchUrl(c.id, p.index)}
                        alt={`Schita poz ${p.index}`}
                        className="max-h-[220px] w-auto"
                        data-testid={`sketch-${p.index}`}
                      />
                    ) : (
                      <span className="text-xs text-slate-300">fara schita</span>
                    )}
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px] text-slate-600">
                    <Badge variant="secondary" className="text-[11px]">{p.latime} × {p.inaltime} mm</Badge>
                    {p.tip_geam && <Badge variant="outline" className="text-[11px]">{p.tip_geam}</Badge>}
                    {p.forma_neregulata && <Badge variant="outline" className="text-[11px] text-amber-600 border-amber-300">forma neregulata</Badge>}
                  </div>
                  {p.observatii && <p className="mt-2 text-[11px] text-slate-400 italic">{p.observatii}</p>}
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
